export namespace KnowledgeApi {
    // Title and summary are spent in every prompt of every conversation once an
    // entry is indexed, so both are tightly capped. Content is only spent when
    // the assistant fetches the entry, which is why it can be far larger.
    export const MAX_TITLE_CHARS = 80;
    export const MAX_SUMMARY_CHARS = 200;
    export const MAX_CONTENT_CHARS = 20000;

    // With title and summary capped, entry count is the only unbounded
    // dimension of the index left, so the ceiling is expressed there: 20
    // entries hold the index under ~6k characters at the worst case.
    export const MAX_ENTRIES = 20;

    // Character thresholds approximating tokens (~4 chars/token). Entries short
    // enough to inline are injected verbatim — a summary of a one-line fact is
    // pure overhead — while everything else is listed in an index and fetched
    // on demand.
    export const INLINE_ENTRY_MAX_CHARS = 300;
    export const INLINE_TOTAL_BUDGET_CHARS = 1200;

    // Which entries reach the assistant whole. Keyed on position rather than on
    // object identity, so a caller holding two equal entries — or the same one
    // twice — still gets an answer per entry.
    export function inlineIndices(entries: readonly { content: string }[]): Set<number> {
        const candidates = entries
            .map((entry, index) => ({ index, length: entry.content.length }))
            .filter((entry) => entry.length <= INLINE_ENTRY_MAX_CHARS)
            .sort((a, b) => a.length - b.length);
        const inline = new Set<number>();
        let total = 0;
        for (const entry of candidates) {
            if (total + entry.length > INLINE_TOTAL_BUDGET_CHARS) break;
            total += entry.length;
            inline.add(entry.index);
        }
        return inline;
    }

    // The two tiers the assistant sees: entries it reads whole, and entries it
    // only sees indexed and has to fetch. The tier is not a property of one
    // entry — short entries are demoted once they collectively overflow the
    // budget — so it is always computed over the whole set.
    export function partition<T extends { content: string }>(entries: readonly T[]): { inline: T[]; indexed: T[] } {
        const inline = inlineIndices(entries);
        return {
            inline: entries.filter((_, index) => inline.has(index)),
            indexed: entries.filter((_, index) => !inline.has(index)),
        };
    }
}
